import {
  addRawDataset,
  fetchTransformedSamplesFromEditor,
  refreshPipeline,
  splitterSetLastTxPayload,
} from 'js/actions';
import { API_GENAI_SERVER_URL } from 'js/constants/api';
import { EDIT_DATASET_WITH_SCHEMA_ENABLED } from 'js/constants/config';
import { EMPTY_DATA_SET_SCHEMA, MOCK_SYSLOG_DETECTED_SCHEMA } from 'js/constants/mockDataSchemas';
import { getSplitterTransform } from 'js/lib/TransformsUtils';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import some from 'lodash/some';
import Constants from '../../Constants';
import * as actionTypes from '../../constants/actionTypes';
import { DELETE_DATA_SET } from '../../constants/processTypes';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
  SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
} from '../../constants/schemaEditor';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterOpTypes,
  SearchFilterTypes,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import { setReferencedResourceIds } from '../../lib/ResourceUtils';
import { buildOutputValidationSchema } from '../../lib/SchemaValidationUtils';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { dataSetSchema, docSchema } from '../../schemas';
import { addResourceApiKey } from '../apiKeys';
import { addProcess, removeProcess } from '../processes';
import { updateCodeContainer } from '../transform';

export const loadDataSets =
  (page, sortCriteria, availableFilters, additionalFilters = [], options = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
    } = getState();

    const aclQueryMode = options?.aclQueryMode ? options.aclQueryMode : accessControl.mode;

    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];

    const allFilters = filters[SearchFilterViews.MY_DATA_SETS]?.concat(additionalFilters) || [];
    const { processedFilters } = processSearchFilters(allFilters, availableFilters);
    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/data_sets';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
        discoverable_data: true,
        //TODO: We'll need to make sure that the nexset_type criteria is aligned with the list view this is attached to. Might need to make this dynamic in a PR where we are showing say nexset_api_compatible only nexsets). Same for the search query.
      };
    } else {
      requestMethod = POST;
      endpoint = `/data_sets/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}&discoverable_data=true`;
      payload = {
        filters: processedFilters,
      };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_SETS_REQUEST,
          actionTypes.GET_DATA_SETS_SUCCESS,
          actionTypes.GET_DATA_SETS_FAILURE,
        ],
        endpoint,
        schema: [dataSetSchema],
        requestMethod,
        payload,
        aclQueryMode,
        requestInfo: { view: SearchFilterViews.MY_DATA_SETS },
      },
    })
      .then((response) => {
        if (isEmpty(processedFilters)) {
          dispatch({
            [CALL_PRIVATE_API]: {
              types: [
                actionTypes.GET_DATA_SETS_SUMMARY_REQUEST,
                actionTypes.GET_DATA_SETS_SUMMARY_SUCCESS,
                actionTypes.GET_DATA_SETS_SUMMARY_FAILURE,
              ],
              endpoint: `/data_sets/summary`,
              requestMethod: GET,
              payload,
              aclQueryMode,
            },
          });
        }
        return response;
      })
      .catch((error) => {
        console.log(`Get Nexsets failed`);
        console.log(error);
      });
  };

export const loadAllDataSets = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DATA_SETS_REQUEST,
        actionTypes.GET_DATA_SETS_SUCCESS,
        actionTypes.GET_DATA_SETS_FAILURE,
      ],
      endpoint: `/data_sets`,
      schema: [dataSetSchema],
      requestMethod: GET,
      payload: {
        page: 1,
        per_page: 50,
        discoverable_data: true,
      },
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  }).catch((error) => {
    console.log(`Get Nexsets failed`);
    console.log(error);
  });
};

export function loadDataFeedDataSets(page, sortCriteria, availableFilters, options = {}) {
  return (dispatch) => {
    const thunk = loadDataSets(page, sortCriteria, availableFilters, [], options);

    return dispatch(thunk);
  };
}

export const loadDataFeedCompatibleDataSets =
  (page, sortCriteria, availableFilters, options = {}) =>
  (dispatch) => {
    return dispatch(
      loadDataSets(
        page,
        sortCriteria,
        availableFilters,
        {
          segment: `${SearchFilterTypes.NEXSET_API_COMPATIBLE}:true`,
          token: SearchFilterTypes.NEXSET_API_COMPATIBLE,
          operator: '',
          value: true,
        },
        options,
      ),
    );
  };

export const loadDataSet =
  (
    id,
    { aclQueryMode, ignoreErrors } = {
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  ) =>
  (dispatch) => {
    return dispatch(fetchDataSet(id, { aclQueryMode, ignoreErrors })).then(() => {
      return dispatch({
        [CALL_PRIVATE_API]: {
          types: [
            actionTypes.GET_DATA_SET_SUMMARY_REQUEST,
            actionTypes.GET_DATA_SET_SUMMARY_SUCCESS,
            actionTypes.GET_DATA_SET_SUMMARY_FAILURE,
          ],
          endpoint: `/data_sets/${id}/summary`,
          requestMethod: GET,
          ignoreErrors,
          aclQueryMode,
        },
      });
    });
  };

export const fetchDataSet =
  (
    dataSetId,
    { aclQueryMode, ignoreErrors } = {
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
      ignoreErrors: false,
    },
  ) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_SET_REQUEST,
          actionTypes.GET_DATA_SET_SUCCESS,
          actionTypes.GET_DATA_SET_FAILURE,
        ],
        endpoint: `/data_sets/${dataSetId}`,
        schema: dataSetSchema,
        requestMethod: GET,
        ignoreErrors,
        aclQueryMode,
        payload: {
          expand: 1,
        },
      },
    });
  };

export const fetchDataSetForRouting = (dataSetId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DATA_SET_FOR_ROUTING_REQUEST,
        actionTypes.GET_DATA_SET_FOR_ROUTING_SUCCESS,
        actionTypes.GET_DATA_SET_FOR_ROUTING_FAILURE,
      ],
      endpoint: `/data_sets/${dataSetId}`,
      schema: dataSetSchema,
      requestMethod: GET,
    },
  });
};

export function loadDataSetSearchSuggestions(filters, availableFilters) {
  return (dispatch, getState) => {
    const { app } = getState();
    const aclQueryMode = app.accessControl.mode;

    const sortCriteria = Constants.SortCriteria.LAST_CREATED_DESC;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];

    const { processedFilters } = processSearchFilters(filters, availableFilters);

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
      discoverable_data: true,
      //TODO: We'll need to make sure that the nexset_type criteria is aligned with the list view this is attached to. Might need to make this dynamic in a PR where we are showing say nexset_api_compatible only nexsets)
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_DATA_SETS_REQUEST,
          actionTypes.SEARCH_DATA_SETS_SUCCESS,
          actionTypes.SEARCH_DATA_SETS_FAILURE,
        ],
        endpoint: `/data_sets/search?${params}`,
        schema: [dataSetSchema],
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: { view: SearchFilterViews.MY_DATA_SETS },
      },
    });
  };
}

// sometimes we need to force create a detected dataset for better ux.
// this is used in addDataSourceFromForm when we want to create a dataset for a new source.

export const addDetectedDataset =
  (info = null, sourceId = null) =>
  (dispatch, getState) => {
    if (!info || !sourceId) {
      dispatch({
        type: actionTypes.ADD_DATASET_FAILURE,
        error: 'Insufficient Nexset information',
      });
      // TODO: create error class
      return Promise.reject(new Error('Insufficient Nexset information'));
    }

    const { name, description } = info;

    const sourceSchema = MOCK_SYSLOG_DETECTED_SCHEMA;

    let payload = {
      name: name ? name : '',
      description: description ? description : '',
      data_source_id: sourceId,
      transform_id: null,
      transform: {
        version: 1,
        data_maps: [],
        transforms: [],
        custom_config: {},
      },
      has_custom_transform: false,
      source_schema: sourceSchema,
      output_schema: sourceSchema,
      output_schema_validation_enabled: false,
      custom_config: {},
    };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.ADD_DATASET_REQUEST,
          actionTypes.ADD_DATASET_SUCCESS,
          actionTypes.ADD_DATASET_FAILURE,
        ],
        endpoint: `/data_sets?expand=1`,
        schema: dataSetSchema,
        requestMethod: POST,
        payload,
      },
    }).catch((error) => {
      console.log('Nexset failed');
      console.log(error);
      return Promise.reject(error);
    });
  };

export const splitDataSet =
  (parentDataSetId, splitterDataSetId, mode, expressions, attr, values, fallback) =>
  (dispatch, getState) => {
    const {
      entities: { dataSets },
    } = getState();
    const splitterDataSet = dataSets[splitterDataSetId];
    const { transform, ast } = getSplitterTransform(
      mode,
      expressions,
      attr,
      values,
      fallback,
      splitterDataSet,
    );
    const payload = {
      name: `Split Nexset ${parentDataSetId}`,
      parent_data_set_id: parentDataSetId,
      transform,
      custom_config: {
        ui: {
          splitSpec: {
            mode,
            expressions,
            attr,
            values,
            fallback,
            ast,
          },
        },
      },
    };

    return dispatch(
      splitterDataSetId ? updateDataSetInfo(splitterDataSetId, payload) : addRawDataset(payload),
    );
  };

export const splitDataSetTest =
  (mode, expressions, attr, values, fallback, inputSamples) => (dispatch) => {
    const { transform } = getSplitterTransform(
      mode,
      expressions,
      attr,
      values,
      fallback,
      null,
      true,
    );

    return dispatch(fetchTransformedSamplesFromEditor(transform, null, null, null, inputSamples))
      .then((response) => {
        dispatch(splitterSetLastTxPayload(transform));
        return response;
      })
      .catch((error) => {
        dispatch(splitterSetLastTxPayload(null));
        return Promise.reject(error);
      });
  };

export const deleteDataset = (id) => (dispatch, getState) => {
  const {
    entities: { dataSets },
  } = getState();
  const dataSet = dataSets[id];

  dispatch(addProcess(DELETE_DATA_SET, `Deleting Nexset "${dataSet.name}"`, { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_DATASET_REQUEST,
        actionTypes.DELETE_DATASET_SUCCESS,
        actionTypes.DELETE_DATASET_FAILURE,
      ],
      endpoint: `/data_sets/${id}`,
      schema: dataSetSchema,
      requestMethod: DELETE,
      requestInfo: {
        id,
        nodeKey: dataSet.flow_node_id,
      },
    },
  })
    .then(() => {
      if (dataSet?.origin_node_id) {
        dispatch(refreshPipeline(dataSet.origin_node_id));
      }
    })
    .finally(() => {
      dispatch(removeProcess(DELETE_DATA_SET, { id }));
    });
};

export const fetchDataSamples =
  (id, count = SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT, live = false) =>
  (dispatch) => {
    let payload = {
      count,
      include_metadata: 1,
      output_only: 1,
    };

    if (live) {
      payload.live = 1;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_SAMPLES_REQUEST,
          actionTypes.GET_DATA_SAMPLES_SUCCESS,
          actionTypes.GET_DATA_SAMPLES_FAILURE,
        ],
        endpoint: `/data_sets/${id}/samples`,
        schema: null,
        requestMethod: GET,
        payload,
        requestInfo: {
          id,
        },
      },
    });
  };

export const updateDataSamples = (id, samples, count) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_DATA_SAMPLES_REQUEST,
        actionTypes.UPDATE_DATA_SAMPLES_SUCCESS,
        actionTypes.UPDATE_DATA_SAMPLES_FAILURE,
      ],
      endpoint: `/data_sets/${id}/samples?count=${count}&include_metadata=1`,
      schema: null,
      requestMethod: PUT,
      payload: samples,
      requestInfo: {
        id,
      },
    },
  });
};

export const addDetectedDatasetSamples =
  (datasetId, samples, hasMetadata = true) =>
  (dispatch) => {
    //this is a temporary solution for adding samples to a detected dataset. we should usually not do it!!!
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_DATA_SAMPLES_REQUEST,
          actionTypes.UPDATE_DATA_SAMPLES_SUCCESS,
          actionTypes.UPDATE_DATA_SAMPLES_FAILURE,
        ],
        endpoint: `/data_sets/${datasetId}/samples`,
        schema: null,
        requestMethod: PUT,
        payload: samples,
        requestInfo: {
          datasetId,
        },
      },
    });
  };

export const fetchCharacteristics = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DATA_CHARACTERISTICS_REQUEST,
        actionTypes.GET_DATA_CHARACTERISTICS_SUCCESS,
        actionTypes.GET_DATA_CHARACTERISTICS_FAILURE,
      ],
      endpoint: `/data_sets/${id}/characteristics`,
      schema: null,
      requestMethod: GET,
      requestInfo: {
        id,
      },
    },
  });
};

export const fetchDataSetUpdateTime = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DATA_SET_UPDATE_TIME_REQUEST,
        actionTypes.GET_DATA_SET_UPDATE_TIME_SUCCESS,
        actionTypes.GET_DATA_SET_UPDATE_TIME_FAILURE,
      ],
      endpoint: `/data_sets/${id}/data_update_time`,
      schema: null,
      requestMethod: GET,
      requestInfo: {
        id,
      },
    },
  });
};

export const updateDataSetInfo =
  (id, payload = {}) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_DATA_SET_INFO_REQUEST,
          actionTypes.UPDATE_DATA_SET_INFO_SUCCESS,
          actionTypes.UPDATE_DATA_SET_INFO_FAILURE,
        ],
        endpoint: `/data_sets/${id}?expand=1`,
        schema: dataSetSchema,
        requestMethod: PUT,
        payload,
      },
    });
  };

export const fetchSamplesForTransform =
  (
    txObject,
    saObject = null,
    transformId,
    outputValidations = null,
    inSamples = {},
    dataSetId,
    include_metadata = true,
    accumulate_schema = true,
    include_annotations = true,
    suppress_errors = true,
  ) =>
  (dispatch, getState) => {
    let endpoint;
    let payload = {
      input: inSamples,
    };
    if (include_metadata === true) {
      payload.include_metadata = 1;
    }

    if (!suppress_errors) {
      payload.suppress_errors = suppress_errors;
    }

    if (transformId) {
      endpoint =
        accumulate_schema === true
          ? `/transforms/${transformId}/transform?accumulate_schema=1`
          : `/transforms/${transformId}/transform`;
    } else {
      endpoint = accumulate_schema === true ? `/transform?accumulate_schema=1` : `/transform`;

      const {
        schemaEditor: {
          toSchema: { flat: toFlat },
        },
      } = getState();

      payload.transform = txObject;

      if (include_annotations === true) {
        payload.output_schema_annotations = saObject;
      }

      if (isEmpty(outputValidations)) {
        payload.output_schema_validation_enabled = false;
        payload.output_validation_schema = {};
      } else {
        payload.output_schema_validation_enabled = true;
        payload.output_validation_schema = buildOutputValidationSchema(outputValidations, toFlat);
      }
    }

    payload = setReferencedResourceIds(payload);

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_TRANSFORMED_SAMPLES_REQUEST,
          actionTypes.GET_TRANSFORMED_SAMPLES_SUCCESS,
          actionTypes.GET_TRANSFORMED_SAMPLES_FAILURE,
        ],
        endpoint,
        schema: null,
        requestMethod: POST,
        payload,
        requestInfo: {
          id: dataSetId,
        },
      },
    });
  };

export const fetchSamplesFromFlatToNestedTransform =
  (txObject, inSamples = {}, dataSetId) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_TRANSFORMED_SAMPLES_FROM_FLAT_TO_NEST_REQUEST,
          actionTypes.GET_TRANSFORMED_SAMPLES_FROM_FLAT_TO_NEST_SUCCESS,
          actionTypes.GET_TRANSFORMED_SAMPLES_FROM_FLAT_TO_NEST_FAILURE,
        ],
        endpoint: `/transform`,
        schema: null,
        requestMethod: POST,
        payload: {
          transform: txObject,
          input: inSamples,
          include_metadata: 1,
        },
        requestInfo: {
          id: dataSetId,
        },
      },
    });
  };

/**
 * Used at
 * - SendDataForm
 */
export const updateDataSetRecipe =
  (
    dataSetId,
    parentDataSetId,
    annotationsObject,
    transformsObject,
    hasCustomTransform,
    transformId,
    transformSpec,
    outputValidations,
    outputSchema,
    rawOutputSchema,
    rawOutputSamples,
    name,
  ) =>
  (dispatch, getState) => {
    //TODO: this is actually updating transforms too!.
    if (!annotationsObject) {
      return dispatch({
        type: actionTypes.UPDATE_DATA_SET_RECIPE_FAILURE,
        error: 'Insufficient annotations information',
      });
    }

    const {
      recipeBuilder,
      entities: { dataSets, transforms },
    } = getState();

    let payload = {
      output_schema_annotations: annotationsObject,
      has_custom_transform: !!hasCustomTransform,
    };

    if (name) {
      payload.name = name;
    }

    if (!transformId) {
      if (transformsObject) {
        payload.transform = transformsObject;
      }

      // if this Nexset previously had a reusable transform applied, unset transform_id
      const prevTransformId = dataSets[dataSetId] && dataSets[dataSetId].transform_id;
      const isPrevTransformReusable =
        transforms && transforms[prevTransformId] && transforms[prevTransformId].reusable;

      if (isPrevTransformReusable) {
        payload.transform_id = null;
      }
    } else {
      payload.transform_id = transformId;
    }

    if (isEmpty(outputValidations)) {
      payload.output_schema_validation_enabled = false;
      payload.output_validation_schema = {};
    } else {
      payload.output_schema_validation_enabled = true;
      payload.output_validation_schema = buildOutputValidationSchema(
        outputValidations,
        outputSchema,
      );
    }

    const isAsync = recipeBuilder?.isAsync;
    const runtimeConfig = dataSets[dataSetId]?.runtime_config;

    if (isAsync) {
      payload.runtime_config = {
        ...runtimeConfig,
        'node.tag': 'async',
      };
    } else if (runtimeConfig?.['node.tag'] === 'async') {
      payload.runtime_config = { ...runtimeConfig };
      delete payload.runtime_config['node.tag'];
    }

    payload.custom_config = { txSpec: transformSpec };

    if (EDIT_DATASET_WITH_SCHEMA_ENABLED) {
      payload.output_schema = isEmpty(rawOutputSchema) ? EMPTY_DATA_SET_SCHEMA : rawOutputSchema;

      if (isEmpty(rawOutputSamples)) {
        const { samples } = getState();
        const nexlaMetaData = samples[parentDataSetId]?.original?.out?.[0]?.nexlaMetaData;
        payload.data_samples = [{ nexlaMetaData, rawMessage: {} }];
      } else {
        payload.data_samples = map(rawOutputSamples, ({ rawMessage, nexlaMetaData }) => ({
          rawMessage,
          nexlaMetaData,
        }));
      }
    }

    payload = setReferencedResourceIds(payload);

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_DATA_SET_RECIPE_REQUEST,
          actionTypes.UPDATE_DATA_SET_RECIPE_SUCCESS,
          actionTypes.UPDATE_DATA_SET_RECIPE_FAILURE,
        ],
        endpoint: `/data_sets/${dataSetId}?expand=1`,
        schema: dataSetSchema,
        requestMethod: PUT,
        payload,
        requestInfo: {
          dataSetId,
        },
      },
    })
      .then((response) => dispatch(copyTxSpecToTransform(response)))
      .catch((error) => {
        console.log('Nexset update failed');
        console.log(error);
        return Promise.reject(error);
      });
  };

export const updateSchemaAnnotations =
  (dataSetId, annotationsObject, transformSpec) => (dispatch) => {
    if (!annotationsObject) {
      return dispatch({
        type: actionTypes.UPDATE_DATA_SET_RECIPE_FAILURE,
        error: 'Insufficient annotations information',
      });
    }

    let payload = {
      output_schema_annotations: annotationsObject,
      custom_config: { txSpec: transformSpec },
    };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_SCHEMA_ANNOTATIONS_REQUEST,
          actionTypes.UPDATE_SCHEMA_ANNOTATIONS_SUCCESS,
          actionTypes.UPDATE_SCHEMA_ANNOTATIONS_FAILURE,
        ],
        endpoint: `/data_sets/${dataSetId}?expand=1`,
        schema: dataSetSchema,
        requestMethod: PUT,
        payload,
        requestInfo: {
          dataSetId,
        },
      },
    })
      .then((response) => dispatch(copyTxSpecToTransform(response)))
      .catch((error) => {
        console.log('Nexset update failed');
        console.log(error);
        return Promise.reject(error);
      });
  };

export const copyTxSpecToTransform = (dataSetResponse) => (dispatch) => {
  return new Promise((resolve, reject) => {
    // if it's not a reusable tx, save a copy of all recipe groups in the
    // transform's custom_config
    const {
      entities: { dataSets },
      result,
    } = dataSetResponse;
    const { transform_id: transformId, custom_config } = dataSets[result];
    const {
      txSpec: { spec: recipeGroups },
    } = custom_config;

    if (
      !transformId ||
      some(
        recipeGroups,
        ({ type, data }) =>
          (type === RECIPE_GROUP_TRANSFORMS_CODE || type === RECIPE_GROUP_TRANSFORMS_AGGREGATE) &&
          data?.type === 'reusable_tx',
      )
    ) {
      resolve(dataSetResponse);
    } else {
      dispatch(updateCodeContainer(transformId, { custom_config }))
        .then(() => resolve(dataSetResponse))
        .catch(reject);
    }
  });
};

export const fetchDatasetDoc = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DATA_SET_DOC_REQUEST,
        actionTypes.GET_DATA_SET_DOC_SUCCESS,
        actionTypes.GET_DATA_SET_DOC_FAILURE,
      ],
      endpoint: `/data_sets/${id}/docs`,
      schema: [docSchema],
      requestMethod: GET,
      payload: {
        expand: 1,
      },
      requestInfo: {
        id,
      },
    },
  });
};

export const updateDatasetDoc = (id, content) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_DATA_SET_DOC_REQUEST,
        actionTypes.UPDATE_DATA_SET_DOC_SUCCESS,
        actionTypes.UPDATE_DATA_SET_DOC_FAILURE,
      ],
      endpoint: `/data_sets/${id}/docs?expand=1`,
      schema: [docSchema],
      requestMethod: POST,
      payload: {
        docs: [
          {
            name: `Doc for Nexset ${id}`,
            description: `Doc for Nexset ${id}`,
            doc_type: 'md',
            text: content,
          },
        ],
      },
      requestInfo: {
        id,
      },
    },
  });
};

//Nexset Sharing actions

export const shareDataset = (id, email) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.SHARE_DATASET_REQUEST,
        actionTypes.SHARE_DATASET_SUCCESS,
        actionTypes.SHARE_DATASET_FAILURE,
      ],
      endpoint: `/data_sets/${id}/sharers`,
      schema: null,
      requestMethod: PUT,
      payload: {
        sharers: [{ email, org_id: '*' }],
      },
    },
  });
};

export const shareDatasetAndFetch = (id, email) => (dispatch) => {
  return dispatch(shareDataset(id, email)).then(
    (response) => {
      return dispatch(fetchDataSet(id));
    },
    (error) => {
      dispatch({
        type: actionTypes.SHARE_DATASET_FAILURE,
        payload: {
          id,
          email,
        },
      });
    },
  );
};

export const replaceDatasetSharers = (id, sharers) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.REPLACE_SUBSCRIBERS_DATASET_REQUEST,
        actionTypes.REPLACE_SUBSCRIBERS_DATASET_SUCCESS,
        actionTypes.REPLACE_SUBSCRIBERS_DATASET_FAILURE,
      ],
      endpoint: `/data_sets/${id}/sharers`,
      schema: null,
      requestMethod: POST,
      payload: {
        sharers,
      },
    },
  });
};

export const replaceDatasetSharersAndFetch = (id, sharers) => (dispatch) => {
  return dispatch(replaceDatasetSharers(id, sharers))
    .then((response) => {
      return dispatch(fetchDataSet(id));
    })
    .catch((error) => {
      dispatch({
        type: actionTypes.REPLACE_SUBSCRIBERS_DATASET_FAILURE,
        payload: {
          id,
          sharers,
        },
      });
    });
};

export const unshareDataset = (id, email) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UNSHARE_DATASET_REQUEST,
        actionTypes.UNSHARE_DATASET_SUCCESS,
        actionTypes.UNSHARE_DATASET_FAILURE,
      ],
      endpoint: `/data_sets/${id}/sharers`,
      schema: null,
      requestMethod: DELETE,
      payload: {
        sharers: [{ email }],
      },
    },
  });
};

export const unshareDatasetAndFetch = (id, email) => (dispatch) => {
  return dispatch(unshareDataset(id, email)).then(
    (response) => {
      return dispatch(fetchDataSet(id));
    },
    (error) => {
      dispatch({
        type: actionTypes.UNSHARE_DATASET_FAILURE,
        payload: {
          id,
          email,
        },
      });
    },
  );
};

export const addDatasetApiKey = (id) => (dispatch) => {
  return dispatch(
    addResourceApiKey('data_sets', id, {
      name: `Key for Nexset ${id}`,
      description: `API Key for fetching data from Nexset ${id}`,
    }),
  ).then(
    (response) => {
      return dispatch(fetchDataSet(id));
    },
    (error) => {
      dispatch({
        type: actionTypes.DATASET_FEED_URL_CREATION_ERROR,
        payload: {
          id,
        },
      });
    },
  );
};

export const accumulateSchema = (input) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ACCUMULATE_SCHEMA_REQUEST,
        actionTypes.ACCUMULATE_SCHEMA_SUCCESS,
        actionTypes.ACCUMULATE_SCHEMA_FAILURE,
      ],
      endpoint: `/transform?accumulate_schema=1`,

      requestMethod: POST,
      payload: {
        input,
        include_metadata: 1,
        transform: {
          version: 1,
          data_maps: [],
          transforms: [],
        },
        output_schema_validation_enabled: false,
        output_validation_schema: {},
      },
    },
  });
};

export const generateSemanticSchema = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GENERATE_SEMANTIC_SCHEMA_REQUEST,
        actionTypes.GENERATE_SEMANTIC_SCHEMA_SUCCESS,
        actionTypes.GENERATE_SEMANTIC_SCHEMA_FAILURE,
      ],
      endpoint: `${API_GENAI_SERVER_URL}/data_sets/${id}/semantic_schema`,
      requestMethod: POST,
      requestInfo: { id },
    },
  })
    .then((response) => {
      return dispatch(fetchDataSet(id));
    })
    .catch((error) => {
      console.error('Error generating semantic schema:', error);
      return Promise.reject(error);
    });
};

export const createApiRequest = (sourceId, method) => (dispatch) => {
  const payload = {
    name: `${method} request`,
    data_source_id: sourceId,
    endpoint_spec: {
      method: method,
    },
  };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.CREATE_API_REQUEST_REQUEST,
        actionTypes.CREATE_API_REQUEST_SUCCESS,
        actionTypes.CREATE_API_REQUEST_FAILURE,
      ],
      endpoint: `/data_sets?expand=1`,
      schema: dataSetSchema,
      requestMethod: POST,
      payload,
    },
  });
};
